import React, { useState, useEffect } from 'react';
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Edit2, 
  X, 
  Check,
  ExternalLink,
  FileText,
  Shield,
  HelpCircle,
  Key
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { API_BASE } from '@/App';
import PasswordSetupPrompt from '@/components/PasswordSetupPrompt';
import { useWhiteLabel, PoweredByBadge } from '@/contexts/WhiteLabelContext';

/**
 * CLARIAS Account & Access Page
 * 
 * This page manages access, not preparation.
 * Purpose: Account trust, legal compliance, subscription transparency, support readiness
 * Feel: Neutral, administrative, calm, non-instructional
 */
const AccountAccessPage = () => {
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '' });
  const [payments, setPayments] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);

  useEffect(() => {
    fetchUserData();
    fetchPaymentHistory();
    fetchSubscriptionStatus();
    fetchAuthStatus();
  }, []);

  const fetchAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token') || JSON.parse(localStorage.getItem('ranker-auth') || '{}').token;
      const { data } = await managedAxios.get(`${API_BASE}/auth/user/auth-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthStatus(data);
    } catch (err) {
      console.error('Failed to fetch auth status:', err);
    }
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await managedAxios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
      setEditData({ name: data.name || '', phone: data.phone || '' });
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await managedAxios.get(`${API_BASE}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { transactions: [] } }));
      setPayments(data.transactions || []);
    } catch (err) {
      setPayments([]);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await managedAxios.get(`${API_BASE}/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: null }));
      setSubscription(data);
    } catch (err) {
      setSubscription(null);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await managedAxios.put(`${API_BASE}/auth/profile`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ ...user, ...editData });
      setEditing(false);
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    } catch (err) {
      toast({ variant: "destructive", title: "Update failed", description: "Please try again." });
    }
  };

  const isUpscPro = user?.role === 'PRO';
  const isSiPro = (user?.pro_exams || []).includes('SI');
  const isPro = isUpscPro || isSiPro;
  const accessDate = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric'
  }) : '—';

  const subscriptionEnd = subscription?.subscription_end
    ? new Date(subscription.subscription_end).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric'
      })
    : null;
  const isLifetime = subscription?.is_lifetime === true;
  const daysRemaining = subscription?.days_remaining ?? null;
  const planLabel = (() => {
    const p = subscription?.subscription_plan;
    if (p === 'clarias_pro_monthly') return 'Monthly';
    if (p === 'clarias_pro_quarterly') return 'Quarterly';
    if (p === 'clarias_pro_yearly') return 'Yearly (Founding)';
    return null;
  })();

  // ── SI PRO status (isolated entitlement; derived from /auth/me) ──
  const siEndDate = user?.si_subscription_end ? new Date(user.si_subscription_end) : null;
  const siEndLabel = siEndDate
    ? siEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const siDaysRemaining = siEndDate
    ? Math.ceil((siEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const siPlanLabel = (() => {
    const p = user?.si_subscription_plan;
    if (p === 'clarias_si_monthly') return 'SI Monthly';
    if (p === 'clarias_si_yearly') return 'SI Yearly';
    if (p === 'admin_grant') return 'SI (Granted)';
    return 'SI PRO';
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Password Setup Modal */}
      <PasswordSetupPrompt 
        open={showPasswordModal} 
        onClose={() => {
          setShowPasswordModal(false);
          fetchAuthStatus(); // Refresh auth status after setting password
        }} 
      />
      
      <div className="max-w-2xl mx-auto p-6 py-10">
        
        {/* ============================================ */}
        {/* SECTION 1: HEADER */}
        {/* ============================================ */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-slate-900">Account & Access</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your account details and access status.
          </p>
        </div>

        {/* ============================================ */}
        {/* SECTION 2: PROFILE INFORMATION */}
        {/* ============================================ */}
        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-slate-800">
                Profile Information
              </CardTitle>
              {!editing ? (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setEditing(true)}
                  className="text-slate-500 hover:text-slate-700 h-8 px-2"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditing(false)}
                    className="text-slate-500 h-8 px-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleSaveProfile}
                    className="text-emerald-600 h-8 px-2"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Full Name */}
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Full Name</p>
                {editing ? (
                  <Input 
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="h-8 mt-1"
                  />
                ) : (
                  <p className="text-sm text-slate-800">{user?.name || '—'}</p>
                )}
              </div>
            </div>

            {/* Email Address */}
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Email Address</p>
                <p className="text-sm text-slate-800">{user?.email || '—'}</p>
              </div>
            </div>

            {/* Mobile Number */}
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Mobile Number</p>
                {editing ? (
                  <Input 
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    placeholder="Not provided"
                    className="h-8 mt-1"
                  />
                ) : (
                  <p className="text-sm text-slate-800">{user?.phone || 'Not provided'}</p>
                )}
              </div>
            </div>

            {/* Password */}
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Password</p>
                {authStatus?.has_password ? (
                  <p className="text-sm text-slate-800">••••••••</p>
                ) : (
                  <p className="text-sm text-amber-600">Not set</p>
                )}
              </div>
              {!authStatus?.has_password && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowPasswordModal(true)}
                  className="text-xs"
                >
                  <Key className="w-3 h-3 mr-1" />
                  Set Password
                </Button>
              )}
            </div>

            {/* Microcopy */}
            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              Used only for account continuity, support, and exam progress tracking.
            </p>
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* SECTION 3: ACCESS STATUS */}
        {/* ============================================ */}
        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium text-slate-800">
              Access Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Access Mode */}
              <div>
                <p className="text-xs text-slate-500 mb-1">Access Mode</p>
                {isPro ? (
                  <Badge className="bg-slate-800 text-white hover:bg-slate-800 font-medium" data-testid="access-mode-badge">
                    {isUpscPro && isSiPro
                      ? 'UPSC PRO + SI PRO'
                      : isSiPro
                        ? 'SI PRO (Full Access Enabled)'
                        : 'PRO (Full Access Enabled)'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-300 text-slate-700 font-medium" data-testid="access-mode-badge">
                    Free (Preview Only)
                  </Badge>
                )}
              </div>

              {/* Secondary Detail */}
              <div className="space-y-2">
                {isUpscPro && (
                  <div className="space-y-2" data-testid="upsc-access-detail">
                    {planLabel && (
                      <p className="text-sm text-slate-600">
                        Plan: <span className="text-slate-800 font-medium">{planLabel}</span>
                      </p>
                    )}
                    {isLifetime ? (
                      <p className="text-sm text-slate-600">
                        Access valid: <span className="text-slate-800 font-medium">Lifetime / no expiry</span>
                      </p>
                    ) : subscriptionEnd ? (
                      <>
                        <p className="text-sm text-slate-600">
                          Access valid till: <span className="text-slate-800 font-medium">{subscriptionEnd}</span>
                          {daysRemaining !== null && daysRemaining >= 0 && (
                            <span className="text-slate-500"> ({daysRemaining} day{daysRemaining === 1 ? '' : 's'} left)</span>
                          )}
                        </p>
                        {daysRemaining !== null && daysRemaining <= 7 && daysRemaining >= 0 && (
                          <div className="flex items-center gap-2 pt-1">
                            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-medium">
                              Expires soon
                            </Badge>
                            <a
                              href="/upgrade"
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline"
                              data-testid="renew-now-link"
                            >
                              Renew now →
                            </a>
                          </div>
                        )}
                        {/* Always-visible "Manage plan" link for Monthly/Quarterly users (covers leftover-days renewal) */}
                        {(planLabel === 'Monthly' || planLabel === 'Quarterly') && !(daysRemaining !== null && daysRemaining <= 7) && (
                          <div className="pt-1">
                            <a
                              href="/upgrade"
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline"
                              data-testid="manage-plan-link"
                            >
                              Manage plan / renew →
                            </a>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">
                        Access valid till: <span className="text-slate-800">—</span>
                      </p>
                    )}
                  </div>
                )}

                {/* SI PRO details (isolated entitlement) */}
                {isSiPro && (
                  <div className={`space-y-2 ${isUpscPro ? 'pt-2 mt-2 border-t border-slate-100' : ''}`} data-testid="si-access-detail">
                    <p className="text-sm text-slate-600">
                      SI Plan: <span className="text-slate-800 font-medium">{siPlanLabel}</span>
                    </p>
                    {siEndLabel ? (
                      <>
                        <p className="text-sm text-slate-600">
                          SI access valid till: <span className="text-slate-800 font-medium">{siEndLabel}</span>
                          {siDaysRemaining !== null && siDaysRemaining >= 0 && (
                            <span className="text-slate-500"> ({siDaysRemaining} day{siDaysRemaining === 1 ? '' : 's'} left)</span>
                          )}
                        </p>
                        {siDaysRemaining !== null && siDaysRemaining <= 7 && siDaysRemaining >= 0 ? (
                          <div className="flex items-center gap-2 pt-1">
                            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-medium">
                              Expires soon
                            </Badge>
                            <a
                              href="/upgrade?exam=si"
                              className="text-xs font-medium text-sky-600 hover:text-sky-700 underline-offset-2 hover:underline"
                              data-testid="si-renew-now-link"
                            >
                              Renew SI PRO →
                            </a>
                          </div>
                        ) : (
                          <div className="pt-1">
                            <a
                              href="/upgrade?exam=si"
                              className="text-xs font-medium text-sky-600 hover:text-sky-700 underline-offset-2 hover:underline"
                              data-testid="si-manage-plan-link"
                            >
                              Manage SI plan / renew →
                            </a>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">
                        SI access valid: <span className="text-slate-800 font-medium">Lifetime / no expiry</span>
                      </p>
                    )}
                  </div>
                )}

                {!isPro && (
                  <p className="text-sm text-slate-600">
                    Access started on: <span className="text-slate-800">{accessDate}</span>
                  </p>
                )}
              </div>

              {/* Microcopy based on access level */}
              <div className="pt-3 border-t border-slate-100">
                {isPro ? (
                  <p className="text-xs text-slate-500">
                    You have full access to verdicts, analytics, and Intensive systems.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      This mode shows how CLARIAS works.<br />
                      Critical verdicts, Intensive tests, and decision analytics are locked.
                    </p>
                    <p className="text-xs text-slate-600 mt-3 font-medium">
                      CLARIAS gives full judgement only when you commit to disciplined preparation.
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* SECTION 4: PAYMENT HISTORY */}
        {/* ============================================ */}
        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium text-slate-800">
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-sm text-slate-500">No payments recorded.</p>
            ) : (
              <div className="space-y-3">
                {payments.map((payment, idx) => {
                  // Razorpay stores amount in paise → convert to rupees with Indian comma formatting
                  const amountRupees = typeof payment.amount === 'number'
                    ? (payment.amount / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })
                    : '—';
                  const productLabel = payment.product_name || ({
                    'clarias_pro_monthly': 'CLARIAS PRO — Monthly',
                    'clarias_pro_quarterly': 'CLARIAS PRO — Quarterly',
                    'clarias_pro_yearly': 'CLARIAS PRO — Yearly (Founding)',
                    'clarias_si_monthly': 'CLARIAS SI PRO — Monthly',
                    'clarias_si_yearly': 'CLARIAS SI PRO — Yearly',
                  }[payment.product_id]) || 'Payment';
                  const statusRaw = (payment.status || payment.payment_status || '').toLowerCase();
                  const isPaid = ['paid', 'success', 'completed', 'captured'].includes(statusRaw);
                  return (
                    <div key={payment.id || idx} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {productLabel}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(payment.date || payment.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isPaid && (
                          <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]">
                            Paid
                          </Badge>
                        )}
                        <p className="text-sm font-semibold text-slate-900">
                          ₹{amountRupees}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* SECTION 5: SUPPORT & LEGAL */}
        {/* ============================================ */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium text-slate-800">
              Support & Legal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Links */}
              <div className="grid grid-cols-2 gap-2">
                <a 
                  href="mailto:support@clarias.in" 
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors py-1"
                >
                  <HelpCircle className="w-4 h-4" />
                  Contact Support
                </a>
                <a 
                  href="/terms" 
                  target="_blank"
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors py-1"
                >
                  <FileText className="w-4 h-4" />
                  Terms of Service
                </a>
                <a 
                  href="/privacy" 
                  target="_blank"
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors py-1"
                >
                  <Shield className="w-4 h-4" />
                  Privacy Policy
                </a>
                <a 
                  href="/refund" 
                  target="_blank"
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors py-1"
                >
                  <FileText className="w-4 h-4" />
                  Refund Policy
                </a>
              </div>

              {/* Support Microcopy */}
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  For account, billing, or data-related queries, contact support@clarias.in
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Actions — visible only to admin emails */}
        {user?.email === 'hariravi28@gmail.com' && (
          <AdminActionsCard />
        )}

        {/* Identity Statement (visible only in dev, or as subtle footer) */}
        <p className="text-center text-xs text-slate-300 mt-8">
          This page manages access. Judgement is earned elsewhere.
        </p>
        
        {/* Powered by CLARIAS - For White-Label Institutes */}
        <PoweredByBadge location="settings" className="mt-4" />

      </div>
    </div>
  );
};

export default AccountAccessPage;

// =========================================================================
// AdminActionsCard — one-click maintenance buttons for admin emails only
// =========================================================================
const AdminActionsCard = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const runBackfill = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await managedAxios.post(
        `${API_BASE}/razorpay/admin/backfill-yearly-2027/me`
      );
      setResult(data);
      toast({
        title: `Updated ${data.modified_count} user(s)`,
        description: data.message,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Backfill failed';
      setResult({ error: msg });
      toast({ title: 'Backfill failed', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-slate-50 border-slate-200 mt-6" data-testid="admin-actions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-slate-700">
          <Shield className="w-4 h-4 text-slate-500" />
          Admin actions
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider ml-auto">
            Admin only
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1 max-w-md">
            <p className="text-sm font-medium text-slate-800">
              Fix Yearly subscriptions (2026 → 2027)
            </p>
            <p className="text-xs text-slate-500">
              One-shot fix for legacy Yearly PRO users whose subscription_end was stored
              with the old 2026 cutoff. Idempotent — safe to run multiple times.
            </p>
          </div>
          <Button
            onClick={runBackfill}
            disabled={loading}
            size="sm"
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="run-backfill-2027-btn"
          >
            {loading ? 'Running…' : 'Run fix'}
          </Button>
        </div>
        {result && !result.error && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1">
            <p className="font-semibold text-emerald-800">
              ✓ {result.modified_count} user(s) updated
            </p>
            {result.affected?.length > 0 && (
              <ul className="text-emerald-700 list-disc list-inside">
                {result.affected.map((a, i) => (
                  <li key={i}>{a.email}</li>
                ))}
              </ul>
            )}
            <p className="text-emerald-600">
              New expiry: {new Date(result.new_subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        )}
        {result?.error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700">
            {result.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
